import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ChevronDown,
  Pin,
  PinOff,
  Edit3,
  Settings,
  Maximize2,
  Check,
  ListFilter,
  Radio,
} from "lucide-react";
import { formatOrgTime } from "@/shared/utils/date/org-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { cn } from "@/ui/lib/utils";
import { useMapPinStore } from "@/map/stores/useMapPinStore";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { useTimelineEditQueueStore } from "@/machines/stores/useTimelineEditQueueStore";
import { useModal } from "@/shared/lib/modal/modal-context";
import type { AvailableTrack } from "@/machines/components/common/timeline-editor/main-preview/main-preview";
import { ROLLING_WINDOW_OPTIONS } from "@/machines/api/queries/events.queries.ts";
import { useTimelineEvents } from "@/machines/api/queries/use-timeline-events";
import { useDelayedLoading } from "@/machines/components/common/timeline-editor/hooks/useDelayedLoading";
import { useDateTimeRangeStore, type DateTimeRangeValue } from "@/shared/stores/useDateTimeRangeStore.ts";
import {
  LIVE_SPAN_OPTIONS,
  formatLiveSpanLabel,
  isTrulyLive,
  liveLagMs,
  liveLeadMs,
} from "@/map/hooks/useRealtimeWindow";
import { useLiveTimelineDomainContext } from "./live-timeline-domain-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import FloatingWindow from "./FloatingWindow";
import { useStickyUpdatedAt } from "@/map/hooks/useStickyUpdatedAt";

export type TimelineWindowMode = "range" | "realtime";

/** How often the map timeline re-polls events so it stays live. */
export const TIMELINE_POLL_MS = 5_000;

/**
 * Roughly three rows fit before the stack starts scrolling — see
 * {@link MapTimelineRow}'s fixed row height (~140px). Keeps the map visible.
 */
const STACK_MAX_H = 460;

interface MapTimelineStackProps {
  /** Active machine — drives the header title, edit mode, and the live badge. */
  activeDeviceMxid: string;
  activeMachineName: string;
  activeMachineColor?: string;
  /** Machines stacked in the body (for the "N machines" hint). */
  /** How many rows are actually rendered — hidden ones are not counted. */
  machineCount: number;
  /** Every machine at the location, for the show/hide menu. */
  machines: { id: number; name: string; device_mxid: string }[];
  /** Machines explicitly opted IN. Anything absent is hidden by default. */
  shownDeviceMxids: string[];
  onToggleMachineShown: (deviceMxid: string) => void;
  /**
   * End of the newest AVAILABLE footage for the active machine. With the shared
   * cursor this is what decides whether the view may call itself live.
   */
  footageEndMs?: number | null;
  /** Track list of the active machine (reported by its row) for the settings modal. */
  availableTracks: AvailableTrack[];
  floating: boolean;
  /** Pull the right edge in so the docked stack clears the right drawer. */
  shiftRight?: boolean;
  /** Push the left edge in so the docked stack clears the left video column (review mode). */
  shiftLeft?: boolean;
  /** "realtime" shows a rolling live window; "range" follows the global date range. */
  mode: TimelineWindowMode;
  onModeChange: (mode: TimelineWindowMode) => void;
  liveSpanMs: number;
  onLiveSpanChange: (spanMs: number) => void;
  /** Realtime "stay live": whether cursor/video live-follow is currently armed. */
  liveFollow: boolean;
  /**
   * An alert is being reviewed — the timeline was switched out of live mode to
   * fetch and show that moment. Drives the dominant "Back to live" button.
   * Deliberately NOT just `mode === "range"`: an operator who picked Range from
   * the switch is doing ordinary historical work and hasn't lost anything.
   */
  reviewingAlert: boolean;
  /** Alert Review V2: seat the controls on the ends of the tracks' own row. */
  compactLayout?: boolean;
  /** Re-arm live-follow and (re)start playback at the live edge. */
  onGoLive: () => void;
  /** The rolling window when in realtime mode (computed by the page). */
  realtimeWindow: DateTimeRangeValue;
  onToggleFloat: () => void;
  onClose: () => void;
  /** The stacked per-machine timeline rows. */
  children: ReactNode;
}

/**
 * Segmented "Live / Range" switch for the timeline's window mode. The Live
 * segment is split: the label activates realtime mode, and a slim chevron
 * opens a menu of view spans (picking a span also switches to realtime).
 */
const ModeSwitch = ({
  mode,
  onModeChange,
  liveSpanMs,
  onLiveSpanChange,
}: {
  mode: TimelineWindowMode;
  onModeChange: (mode: TimelineWindowMode) => void;
  liveSpanMs: number;
  onLiveSpanChange: (spanMs: number) => void;
}) => {
  const { t } = useTranslation("map");
  const liveLabel = formatLiveSpanLabel(liveSpanMs);
  const isRealtime = mode === "realtime";
  const segmentClasses = (active: boolean) =>
    cn(
      "rounded px-1.5 py-0.5 transition-colors",
      active
        ? "bg-primary text-primary-foreground"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
    );

  return (
    <span className="flex shrink-0 items-center rounded-md border border-border p-0.5 text-[11px]">
      <span className={cn("flex items-center", segmentClasses(isRealtime), "px-0 py-0")}>
        <button
          type="button"
          title={t("timeline.mode.liveTitle", { span: liveLabel })}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onModeChange("realtime")}
          className="rounded-l px-1.5 py-0.5"
        >
          {t("timeline.mode.live", { span: liveLabel })}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("timeline.mode.spanAria")}
              title={t("timeline.mode.spanAria")}
              onMouseDown={(e) => e.stopPropagation()}
              className={cn(
                "rounded-r py-0.5 pl-0.5 pr-1 transition-colors",
                isRealtime ? "hover:bg-primary/80" : "hover:text-foreground",
              )}
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onMouseDown={(e) => e.stopPropagation()}>
            {LIVE_SPAN_OPTIONS.map(({ ms, label }) => (
              <DropdownMenuItem
                key={ms}
                className="gap-2 text-xs"
                onClick={() => {
                  onLiveSpanChange(ms);
                  onModeChange("realtime");
                }}
              >
                <Check className={cn("h-3 w-3", ms === liveSpanMs ? "opacity-100" : "opacity-0")} />
                {t("timeline.mode.spanOption", { span: label })}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
      <button
        type="button"
        title={t("timeline.mode.rangeTitle")}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => onModeChange("range")}
        className={segmentClasses(mode === "range")}
      >
        {t("timeline.mode.range")}
      </button>
    </span>
  );
};

/**
 * "Live · HH:mm:ss" — when the timeline's data last landed, and whether it is
 * currently waiting on more.
 *
 * The fetching state is DELAYED rather than raw. A poll takes 176–267 ms and
 * runs every 5 s, so pinging on every `isFetching` meant the badge blinked
 * perpetually and said nothing: a fetch that had stalled for ten seconds looked
 * exactly like one that landed instantly. Now it stays quiet through a normal
 * poll and speaks only when the wait is real — which is also the case where an
 * operator opening an alert is looking at segments that have not caught up yet.
 */
const LiveBadge = ({ updatedAt, isFetching }: { updatedAt: number; isFetching: boolean }) => {
  const { t } = useTranslation("map");
  const waiting = useDelayedLoading(isFetching);
  if (!updatedAt) return null;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] tabular-nums transition-colors",
        waiting
          ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
          : "border-border bg-muted/60 text-muted-foreground",
      )}
      title={
        waiting
          ? t("timeline.badge.waitingTitle")
          : t("timeline.badge.idleTitle")
      }
    >
      <span className="relative flex h-1.5 w-1.5">
        {waiting && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
        )}
        <span
          className={cn(
            "relative inline-flex h-1.5 w-1.5 rounded-full",
            waiting ? "bg-amber-500" : "bg-emerald-500",
          )}
        />
      </span>
      {waiting
        ? t("timeline.badge.updating")
        : t("timeline.badge.live", { time: formatOrgTime(updatedAt, "HH:mm:ss") })}
    </span>
  );
};

/**
 * Realtime playback-follow control. When the cursor + primary video are pinned
 * to the live edge AND playing, shows a pulsing "Live" chip. Otherwise (the user
 * scrubbed back or paused) shows a "Go live" button that jumps back to the edge
 * and resumes playback. Distinct from the data-freshness {@link LiveBadge}: this
 * is about where playback sits, not when data last refreshed.
 */
/**
 * Least time between self-heal re-syncs.
 *
 * Long enough that a site whose footage genuinely cannot keep up re-syncs
 * periodically rather than looping — Go-live refetches around the live edge, and
 * doing that every render on a struggling pipeline would be its own outage.
 */
const SELF_HEAL_MIN_INTERVAL_MS = 30_000;

/** "5m" / "45s" — how far behind the live edge, for the Go-live control. */
const formatLag = (lagMs: number): string =>
  lagMs >= 60_000 ? `${Math.round(lagMs / 60_000)}m` : `${Math.round(lagMs / 1000)}s`;

const GoLiveControl = ({
  isLive,
  lagMs,
  onGoLive,
}: {
  isLive: boolean;
  /** Distance from the live edge when NOT live — surfaced so the gap is visible. */
  lagMs: number | null;
  onGoLive: () => void;
}) => {
  const { t } = useTranslation("map");

  if (isLive) {
    return (
      <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-medium text-red-500">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
        </span>
        {t("timeline.goLive.live")}
      </span>
    );
  }
  // Behind the edge by a known amount: say so. "Go live" alone gave the operator
  // no way to tell a tile that is a few seconds behind from one stuck five
  // minutes back, which is exactly the distinction that matters when deciding
  // whether what they are watching is current.
  const behind = lagMs != null && lagMs > 0 ? formatLag(lagMs) : null;
  return (
    <button
      type="button"
      title={
        behind
          ? t("timeline.goLive.behindTitle", { behind })
          : t("timeline.goLive.title")
      }
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onGoLive}
      className="flex shrink-0 items-center gap-1 rounded-full border border-red-500/40 px-2 py-0.5 text-[11px] font-medium text-red-500 transition-colors hover:bg-red-500/10"
    >
      <Radio className="h-3 w-3" />
      {behind ? t("timeline.goLive.behind", { behind }) : t("timeline.goLive.action")}
    </button>
  );
};

/**
 * Prominent escape hatch out of Range mode. Selecting an alert switches the
 * timeline to Range (so the alert's moment is actually fetched and scrubbable),
 * which silently disarms live-follow — this is the way back. Deliberately large
 * and floated above the docked stack rather than tucked into the header chrome:
 * an operator who drilled into an alert should never have to hunt for "live".
 * The floating (popped-out) stack shows the compact variant in its title row,
 * since there is no docked shell to float above.
 */
const BackToLiveButton = ({
  t,
  compact = false,
  onClick,
}: {
  t: TFunction<"map">;
  compact?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    title={t("timeline.backToLive.title")}
    onMouseDown={(e) => e.stopPropagation()}
    onClick={onClick}
    className={cn(
      "flex shrink-0 items-center gap-2 rounded-full bg-red-500 font-semibold text-white shadow-lg transition-colors hover:bg-red-600",
      compact ? "px-2.5 py-1 text-[11px]" : "px-4 py-2 text-sm",
    )}
  >
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
    </span>
    {t("timeline.backToLive.label")}
  </button>
);

const IconButton = ({
  active,
  disabled,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onMouseDown={(e) => e.stopPropagation()}
    onClick={onClick}
    className={cn(
      "rounded-md p-1 transition-colors",
      active
        ? "bg-primary text-primary-foreground"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
      disabled && "opacity-40 cursor-not-allowed hover:bg-transparent",
    )}
  >
    {children}
  </button>
);

/**
 * Pick which machines get a timeline row.
 *
 * The stack is the tallest thing on the page and each row costs its own event,
 * truck-event and video-availability queries, so an operator watching two
 * machines out of eight should be able to say so. Hiding is not cosmetic: the
 * page stops rendering the row entirely and its requests stop with it.
 *
 * The ACTIVE machine cannot be hidden — it owns the cursor the whole stack is
 * aligned to and the video that drives it, so hiding it would leave every other
 * row following a playhead nobody can see.
 */
const TimelineVisibilityMenu = ({
  machines,
  shownDeviceMxids,
  activeDeviceMxid,
  onToggle,
}: {
  machines: { id: number; name: string; device_mxid: string }[];
  shownDeviceMxids: string[];
  activeDeviceMxid: string;
  onToggle: (deviceMxid: string) => void;
}) => {
  const { t } = useTranslation("map");
  const shown = new Set(shownDeviceMxids);
  const shownCount = machines.filter(
    (m) => m.device_mxid === activeDeviceMxid || shown.has(m.device_mxid),
  ).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("timeline.visibility.aria")}
          title={t("timeline.visibility.aria")}
          onMouseDown={(e) => e.stopPropagation()}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ListFilter className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          {t("timeline.visibility.count", {
            shown: formatNumber(shownCount),
            total: formatNumber(machines.length),
          })}
        </div>
        {machines.map((m) => {
          const isActive = m.device_mxid === activeDeviceMxid;
          const isShown = isActive || shown.has(m.device_mxid);
          return (
            <DropdownMenuItem
              key={m.id}
              disabled={isActive}
              onSelect={(e) => {
                // Keep the menu open so several rows can be toggled at once.
                e.preventDefault();
                if (!isActive) onToggle(m.device_mxid);
              }}
              className="gap-2 text-xs"
            >
              <Check className={cn("h-3.5 w-3.5", !isShown && "opacity-0")} />
              <span className="truncate">{m.name}</span>
              {isActive && (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {t("timeline.visibility.active")}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Bottom-docked (or floating) SHELL for the `/map` live preview — hosts a
 * vertically-scrollable stack of per-machine timeline rows (passed as
 * `children`). Owns the shared chrome: the Live/Range switch, live badge, the
 * pin/pop-out/close controls, and the Edit-Mode / Track-Settings icons.
 *
 * Edit mode and the settings modal target the ACTIVE machine: this shell must
 * render inside the ACTIVE machine's `MachineTimelineProvider`, so
 * `useTimelineContext()` here reads the active machine's edit state. Switching
 * the active machine resets edit mode and clears the (global) edit queue so
 * pending edits can't leak across machines.
 */
const MapTimelineStack = ({
  activeDeviceMxid,
  activeMachineName,
  activeMachineColor,
  machineCount,
  machines,
  shownDeviceMxids,
  onToggleMachineShown,
  footageEndMs = null,
  availableTracks,
  floating,
  shiftRight = false,
  shiftLeft = false,
  mode,
  onModeChange,
  liveSpanMs,
  onLiveSpanChange,
  liveFollow,
  reviewingAlert,
  compactLayout = false,
  onGoLive,
  realtimeWindow,
  onToggleFloat,
  onClose,
  children,
}: MapTimelineStackProps) => {
  const { t } = useTranslation("map");
  const { value } = useDateTimeRangeStore();
  const { isEditMode, setIsEditMode, setSelectedEventForEdit, isVideoPlaying, requestPlay } =
    useTimelineContext();
  const clearChanges = useTimelineEditQueueStore((s) => s.clearChanges);
  const { openModal } = useModal();

  // How far playback trails the newest footage. The cursor comes from the shared
  // domain, which samples it once a second — reading it reactively would
  // re-render this shell on every playback frame, the storm the cursor store was
  // split out to prevent.
  const { cursorMs } = useLiveTimelineDomainContext();
  const liveLag = liveLagMs(footageEndMs, cursorMs);

  // Self-heal a live view that has fallen off its own window.
  //
  // While follow is armed the app is claiming to be live, so the playhead has to
  // be somewhere the operator can SEE. The window shows `liveSpanMs` with the
  // cursor parked `liveLeadMs` from its right edge, so anything lagging further
  // than that is drawn off the left edge — and the badge then reports a number
  // the timeline cannot possibly be showing ("12m behind" on a 10m window). That
  // is not a big lag, it is an incoherent state: whatever put us there (a stalled
  // tile, a backgrounded tab, footage that arrived in a burst) has already
  // failed, and no amount of waiting brings the cursor back on screen, because
  // nothing pulls it forward except playback that is evidently not keeping up.
  //
  // So re-run the same Go-live the operator would click. Guarded three ways:
  // only while follow is ARMED (a deliberate review must never be yanked back),
  // only once the lag is genuinely off-window, and at most once per interval so
  // a site whose footage cannot keep up re-syncs periodically instead of looping.
  const lastSelfHealRef = useRef(0);
  useEffect(() => {
    if (!liveFollow || liveLag == null) return;
    const offWindowMs = liveSpanMs - liveLeadMs(liveSpanMs);
    if (liveLag <= offWindowMs) return;
    const now = Date.now();
    if (now - lastSelfHealRef.current < SELF_HEAL_MIN_INTERVAL_MS) return;
    lastSelfHealRef.current = now;
    onGoLive();
  }, [liveFollow, liveLag, liveSpanMs, onGoLive]);

  // Layout lock (persisted, shared with the other map panels): freeze the
  // docked shell's horizontal insets (`shiftLeft`/`shiftRight`) at pin time so
  // a docking video or an opening category drawer can't slide it around. Rows
  // keep streaming — only the geometry is frozen. (Floating mode returns early
  // and ignores these.)
  const isPinned = useMapPinStore((s) => s.isPinned("timeline"));
  const togglePin = useMapPinStore((s) => s.togglePin);
  const [frozenShift, setFrozenShift] = useState<{ left: boolean; right: boolean } | null>(null);
  useEffect(() => {
    if (isPinned && frozenShift == null) setFrozenShift({ left: shiftLeft, right: shiftRight });
    else if (!isPinned && frozenShift != null) setFrozenShift(null);
  }, [isPinned, shiftLeft, shiftRight, frozenShift]);
  const effectiveShiftLeft = frozenShift?.left ?? shiftLeft;
  const effectiveShiftRight = frozenShift?.right ?? shiftRight;

  const isRealtime = mode === "realtime";
  const fromDate = isRealtime ? realtimeWindow.fromDate : value.fromDate;
  const toDate = isRealtime ? realtimeWindow.toDate : value.toDate;

  // Same query key as the active row's fetches — this adds an observer so the
  // header badge can show the active machine's freshness state.
  const { dataUpdatedAt, isFetching } = useTimelineEvents(
    {
      device_mxid: activeDeviceMxid,
      start_time: fromDate,
      end_time: toDate,
      page: 1,
      itemsPerPage: 20000,
    },
    { ...(isRealtime ? ROLLING_WINDOW_OPTIONS : {}), live: isRealtime },
  );
  // Keep the last real timestamp visible while a shifted rolling window loads
  // (a fresh query key reports dataUpdatedAt=0 until its first response).
  // Sticky across a shifted rolling window, dropped on a machine switch — see
  // `useStickyUpdatedAt`.
  const lastUpdated = useStickyUpdatedAt(dataUpdatedAt, activeDeviceMxid);

  // Returning to live is exactly the Go-live action plus the mode switch back to
  // realtime (owned by the page's onGoLive), with playback re-armed the same way
  // the header's Go-live chip does it.
  const handleBackToLive = () => {
    requestPlay();
    onGoLive();
  };

  const toggleEdit = () => {
    if (isEditMode) setSelectedEventForEdit(null);
    setIsEditMode(!isEditMode);
  };

  // Switching the active machine exits edit mode and clears the global edit
  // queue, so a half-finished edit on the previous machine can't be saved
  // against the new one. (Also runs harmlessly on first mount.)
  useEffect(() => {
    setIsEditMode(false);
    setSelectedEventForEdit(null);
    clearChanges();
  }, [activeDeviceMxid, setIsEditMode, setSelectedEventForEdit, clearChanges]);

  // `Shift+E` toggles timeline edit mode (the page's plain-letter shortcuts use
  // `e` for the Events panel, so the edit toggle is shifted).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.shiftKey &&
        e.key.toLowerCase() === "e" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        toggleEdit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // toggleEdit closes over the latest isEditMode each render; re-bind to track it.
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Who this stack is showing: colour dot, machine name, "· Timelines". */
  const identity = (
    <>
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: activeMachineColor || "#EF4444" }}
      />
      <span className="truncate font-medium">{activeMachineName}</span>
      <span className="shrink-0 text-muted-foreground">
        {t("timeline.identitySuffix")}
        {machineCount > 1 ? ` (${formatNumber(machineCount)})` : ""}
      </span>
    </>
  );

  /** Live/Range, the live badge, and the track controls — operable, and needed
   *  in both layouts. Kept apart from `identity` so the compact row can arrange
   *  the two down its left edge instead of stacking a header above the tracks. */
  const controls = (
    <>
      <ModeSwitch
        mode={mode}
        onModeChange={onModeChange}
        liveSpanMs={liveSpanMs}
        onLiveSpanChange={onLiveSpanChange}
      />
      {isRealtime && (
        <GoLiveControl
          // Liveness is about WHERE playback is, not merely that the element is
          // playing — see `isTrulyLive`. A tile five minutes behind used to show
          // a pulsing "Live", which made the one indicator an operator relies on
          // to know they are watching NOW the one they could not trust.
          isLive={isTrulyLive(liveFollow, isVideoPlaying, liveLag)}
          lagMs={liveLag}
          onGoLive={() => {
            requestPlay();
            onGoLive();
          }}
        />
      )}
      {reviewingAlert && floating && (
        <BackToLiveButton t={t} compact onClick={handleBackToLive} />
      )}
      <LiveBadge updatedAt={lastUpdated} isFetching={isFetching} />
      <span className="ml-1 flex items-center gap-0.5">
        {machines.length > 1 && (
          <TimelineVisibilityMenu
            machines={machines}
            shownDeviceMxids={shownDeviceMxids}
            activeDeviceMxid={activeDeviceMxid}
            onToggle={onToggleMachineShown}
          />
        )}
        <IconButton active={isEditMode} label={t("timeline.editActive")} onClick={toggleEdit}>
          <Edit3 className="h-4 w-4" />
        </IconButton>
        <IconButton
          label={t("timeline.trackSettings")}
          disabled={isEditMode || availableTracks.length === 0}
          onClick={() => openModal("timeline-track-settings", { availableTracks })}
        >
          <Settings className="h-4 w-4" />
        </IconButton>
      </span>
    </>
  );

  /** Pin / pop-out / hide — the shell's own controls, in either layout. */
  const shellControls = (
    <>
      <IconButton
        active={isPinned}
        label={isPinned ? t("timeline.unpin") : t("timeline.pin")}
        onClick={() => togglePin("timeline")}
      >
        {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
      </IconButton>
      <IconButton label={t("timeline.popOut")} onClick={onToggleFloat}>
        <Maximize2 className="h-4 w-4" />
      </IconButton>
      <IconButton label={t("timeline.hide")} onClick={onClose}>
        <ChevronDown className="h-4 w-4" />
      </IconButton>
    </>
  );

  // Driven by the MODE, not by "an alert happens to be open": Alert · review
  // keeps its header, V2 gives the height to the frames.
  const compact = compactLayout && !floating;

  const body = (
    <div className="flex flex-col gap-1 overflow-y-auto pr-1" style={{ maxHeight: STACK_MAX_H }}>
      {children}
    </div>
  );

  if (floating) {
    return (
      <FloatingWindow
        title={
          <>
            {identity}
            {controls}
          </>
        }
        width={720} bodyClassName="p-3" onDock={onToggleFloat} onClose={onClose}>
        {body}
      </FloatingWindow>
    );
  }

  return (
    <div
      className={cn(
        "absolute bottom-4 z-20 flex flex-col rounded-xl border border-border bg-background/95 shadow-[0_8px_32px_rgba(0,0,0,0.18)] backdrop-blur transition-[left,right] duration-200 animate-in slide-in-from-bottom-2 fade-in",
        // Full width for the whole of V2: the camera lives in a review PANEL,
        // not a full-height dock, so nothing ever sits beside the timeline in
        // this mode and there is no column to clear.
        compact || !effectiveShiftLeft ? "left-4" : "left-[356px]",
        compact || !effectiveShiftRight ? "right-4" : "right-[356px]",
      )}
    >
      {/* Floated above the shell's top edge, so it clears the stack whatever its
          height. The shell is absolutely positioned, so it is this button's
          containing block. */}
      {reviewingAlert && (
        <div className="absolute -top-14 left-1/2 z-30 -translate-x-1/2">
          <BackToLiveButton t={t} onClick={handleBackToLive} />
        </div>
      )}
      {/* Alert review puts the controls on the ENDS of the tracks' own row
          instead of on a row above them. The header costs ~50px of height, and
          in review that height is what the frame panels want — the operator is
          reading pictures, not administering the timeline. */}
      {compact ? (
        // No machine name here: the review already names the machine on both
        // panels, and repeating it costs width the tracks want.
        <div className="flex items-stretch gap-2 p-2">
          {/* Identity and controls at the START of the tracks' own row — the
              point of the compact layout is that nothing sits ABOVE the
              timeline, not that the machine goes unnamed. */}
          <div className="flex w-44 shrink-0 flex-col justify-center gap-1.5 text-sm">
            <div className="flex min-w-0 items-center gap-1.5">{identity}</div>
            <div className="flex flex-wrap items-center gap-1">{controls}</div>
          </div>
          <div className="min-w-0 flex-1">{body}</div>
          <div className="flex shrink-0 flex-col justify-center gap-1">{shellControls}</div>
        </div>
      ) : (
        <>
          <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              {identity}
              {controls}
            </div>
            <div className="flex shrink-0 items-center gap-1">{shellControls}</div>
          </header>

          <div className="p-2">{body}</div>
        </>
      )}
    </div>
  );
};

export default MapTimelineStack;
